// Smooth scroll to in-page anchors, offset by the sticky header's live height (kept in the
// `--kd-scroll-offset` CSS variable).
export default function initSmoothScroll() {
  const HEADER_SELECTOR = '.site-header';
  const EXTRA_GAP = 16; // small breathing room below the header

  // Header offset
  function getHeaderHeight() {
    const header = document.querySelector(HEADER_SELECTOR);
    if (!header) return 0;
    const pos = window.getComputedStyle(header).position;
    // Only sticky/fixed headers overlay the content and need compensating.
    if (pos !== 'sticky' && pos !== 'fixed') return 0;
    return Math.round(header.getBoundingClientRect().height);
  }

  // Keep the CSS variable in sync so native anchor jumps (and any
  // scrollIntoView) clear the header too.
  function syncOffsetVar() {
    document.documentElement.style.setProperty(
      '--kd-scroll-offset',
      getHeaderHeight() + EXTRA_GAP + 'px'
    );
  }

  // Target resolution
  function resolveTarget(hash) {
    if (!hash || hash === '#') return null;
    const id = decodeURIComponent(hash.slice(1));
    if (!id) return null;
    const safe = window.CSS && CSS.escape ? CSS.escape(id) : id;
    return (
      document.getElementById(id) ||
      document.querySelector('a[name="' + safe + '"]')
    );
  }

  function scrollToTarget(target) {
    const offset = getHeaderHeight() + EXTRA_GAP;

    // When global Lenis is active, let it own the animation so the glide
    // matches the site-wide wheel smoothing; otherwise use native scrolling.
    if (window.__kdLenis) {
      window.__kdLenis.scrollTo(target, { offset: -offset });
      return;
    }

    const top =
      target.getBoundingClientRect().top +
      window.pageYOffset -
      offset;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  // Click handling (event delegation)
  document.addEventListener('click', (event) => {
    // Ignore modified clicks (open-in-new-tab, etc.).
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest('a[href]');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href) return;

    // Accept "#id" directly, or a full/relative URL whose path matches the
    // current page and that carries a hash ("/about-us/#id").
    let hash = '';
    if (href.charAt(0) === '#') {
      hash = href;
    } else if (href.indexOf('#') !== -1) {
      let url;
      try {
        url = new URL(href, window.location.href);
      } catch (e) {
        return;
      }
      if (
        url.hash &&
        url.pathname === window.location.pathname &&
        url.host === window.location.host
      ) {
        hash = url.hash;
      }
    }
    if (!hash) return;

    const target = resolveTarget(hash);
    if (!target) return; // no matching element — let the browser decide

    event.preventDefault();
    scrollToTarget(target);

    // Reflect the hash in the address bar without an extra native jump.
    if (window.history && history.pushState) {
      history.pushState(null, '', hash);
    }

    // Accessibility: move focus to the target without re-scrolling.
    const hadTabindex = target.hasAttribute('tabindex');
    if (!hadTabindex) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    if (!hadTabindex) {
      target.addEventListener(
        'blur',
        function handleBlur() {
          target.removeAttribute('tabindex');
          target.removeEventListener('blur', handleBlur);
        },
        { once: true }
      );
    }
  });

  // Initial load with a hash in the URL: the browser's native jump ignores
  // the sticky header, so re-scroll with the correct offset once settled.
  function handleInitialHash() {
    if (!window.location.hash) return;
    const target = resolveTarget(window.location.hash);
    if (!target) return;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => scrollToTarget(target));
    });
  }

  // Wire up
  syncOffsetVar();
  window.addEventListener('resize', syncOffsetVar);
  window.addEventListener('load', () => {
    syncOffsetVar();
    handleInitialHash();
  });
}
